import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { postMessageToParent } from '../message-bus/postMessageToParent';

export type AnalyticsUserMetadata = {
	isExtensionPinned?: boolean;
	ntpEnabled?: boolean;
	defaultTab?: string | null;
	lastUsedTab?: string | null;
};

export type AnalyticsUserMetadataContextType = {
	userMetadata: AnalyticsUserMetadata | undefined;
	setUserMetadata: React.Dispatch<React.SetStateAction<AnalyticsUserMetadata | undefined>>;
};

export const AnalyticsUserMetadataContext = createContext<
	AnalyticsUserMetadataContextType | undefined
>(undefined);

export const AnalyticsUserMetadataProvider = ({
	children,
}: PropsWithChildren): React.JSX.Element => {
	const [userMetadata, setUserMetadata] = useState<AnalyticsUserMetadata>();

	useEffect(() => {
		const messageHandler = (event: MessageEvent): void => {
			if (event.data.type === 'GET_USER_METADATA_RESPONSE') {
				setUserMetadata({
					isExtensionPinned: event.data.isExtensionPinned,
					ntpEnabled: event.data.ntpEnabled,
				});
			}
		};

		window.addEventListener('message', messageHandler, false);

		postMessageToParent({ type: 'GET_USER_METADATA_REQUEST' });

		return (): void => {
			window.removeEventListener('message', messageHandler);
		};
	}, []);

	return (
		<AnalyticsUserMetadataContext.Provider value={{ userMetadata, setUserMetadata }}>
			{children}
		</AnalyticsUserMetadataContext.Provider>
	);
};

export const useAnalyticsUserMetadata = (): AnalyticsUserMetadataContextType | undefined => {
	/// context may be undefined if outside context provider, extension does not have message handler and cannot respond, or still awaiting response
	return useContext(AnalyticsUserMetadataContext);
};
